import { faLock } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { FilterConfig, FilterOption } from '@supply-mover-2-0/core';
import React, { useEffect, useRef, useState } from 'react';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { cn } from '../../utils/cn';
import { Text } from '../base/Text';

interface BaseNumberRangeProps {
	filterType: string;
	config: FilterConfig;
	selectedValues: FilterOption[];
	onValuesChange: (values: FilterOption[]) => void;
	disabled?: boolean;
	isModalOpen?: boolean;
	lockedValues?: FilterOption[];
	debounceMs?: number;
	size?: 'xs' | 'sm' | 'md' | 'lg';
}

interface RangeData {
	min: number | null;
	max: number | null;
}

const formatRangeLabel = (
	range: RangeData,
	suffix: string,
	placeholder: string,
): string => {
	if (range.min != null && range.max != null) {
		return `${range.min}${suffix} – ${range.max}${suffix}`;
	}
	if (range.min != null) {
		return `≥ ${range.min}${suffix}`;
	}
	if (range.max != null) {
		return `≤ ${range.max}${suffix}`;
	}
	return placeholder;
};

/**
 * Numeric min/max range filter — two number inputs, either/both optional.
 * Stores the selection as a single FilterOption with `data: { min, max }`
 * to match the shape of BaseDateRange.
 */
export const BaseNumberRange: React.FC<BaseNumberRangeProps> = ({
	filterType: _filterType,
	config,
	selectedValues,
	onValuesChange,
	disabled = false,
	isModalOpen = false,
	lockedValues = [],
	debounceMs = 400,
	size = 'md',
}) => {
	const colors = useThemeColors();
	const { t } = useTranslation();

	const labelSizeClass = {
		xs: 'text-xs',
		sm: 'text-sm',
		md: 'text-sm',
		lg: 'text-base',
	}[size];

	const inputSizeClass = {
		xs: 'py-1 text-xs',
		sm: 'py-1.5 text-sm',
		md: 'py-2 text-sm',
		lg: 'py-2.5 text-base',
	}[size];

	const isLocked = lockedValues.length > 0;
	const current = (selectedValues[0] ?? lockedValues[0]) as
		| (FilterOption & { data?: RangeData })
		| undefined;
	const currentMin = current?.data?.min ?? null;
	const currentMax = current?.data?.max ?? null;

	// Local input state so the user can type freely without round-tripping
	// a filter request on every keystroke; we debounce the commit.
	const [minInput, setMinInput] = useState<string>(
		currentMin != null ? String(currentMin) : '',
	);
	const [maxInput, setMaxInput] = useState<string>(
		currentMax != null ? String(currentMax) : '',
	);

	const onValuesChangeRef = useRef(onValuesChange);
	onValuesChangeRef.current = onValuesChange;

	// Sync local inputs if the filter is cleared/updated externally
	useEffect(() => {
		setMinInput(currentMin != null ? String(currentMin) : '');
		setMaxInput(currentMax != null ? String(currentMax) : '');
	}, [currentMin, currentMax]);

	// Debounced commit to parent — only fires when values actually differ.
	useEffect(() => {
		const handle = setTimeout(() => {
			const parsedMin = minInput.trim() === '' ? null : Number(minInput);
			const parsedMax = maxInput.trim() === '' ? null : Number(maxInput);

			const minValid = parsedMin == null || Number.isFinite(parsedMin);
			const maxValid = parsedMax == null || Number.isFinite(parsedMax);
			if (!minValid || !maxValid) {
				return;
			}

			if (parsedMin === currentMin && parsedMax === currentMax) {
				return;
			}

			if (parsedMin == null && parsedMax == null) {
				onValuesChangeRef.current([]);
				return;
			}

			const suffix = typeof config.suffix === 'string' ? config.suffix : '';
			const placeholder = config.placeholder ?? 'Any';
			const label = formatRangeLabel(
				{ min: parsedMin, max: parsedMax },
				suffix,
				placeholder,
			);

			onValuesChangeRef.current([
				{
					id: 'custom',
					label,
					data: { min: parsedMin, max: parsedMax },
				},
			]);
		}, debounceMs);

		return () => clearTimeout(handle);
	}, [
		minInput,
		maxInput,
		currentMin,
		currentMax,
		config.suffix,
		config.placeholder,
		debounceMs,
	]);

	const inputBase = cn(
		'w-full rounded border px-2',
		inputSizeClass,
		colors.classes.borderBase,
		colors.classes.backgroundBase,
		colors.classes.textPrimary,
		(disabled || isLocked) && 'opacity-60 cursor-not-allowed',
	);

	return (
		<div className="mb-4">
			{config.label && (
				<Text
					as="label"
					className={cn(
						'block font-medium mb-2',
						labelSizeClass,
						colors.classes.textPrimary,
					)}
				>
					{t(config.label)}
					{config.required && (
						<Text as="span" className={cn(colors.classes.textDanger, 'ml-1')}>
							*
						</Text>
					)}
				</Text>
			)}

			<div className="flex items-center gap-2">
				{isLocked && (
					<FontAwesomeIcon
						icon={faLock}
						className={cn('w-4 h-4', colors.classes.textSecondary)}
					/>
				)}

				<input
					type="number"
					inputMode="decimal"
					className={inputBase}
					value={minInput}
					min={config.min}
					max={config.max}
					step={config.step ?? 'any'}
					disabled={disabled || isModalOpen || isLocked}
					placeholder={t('Min')}
					onChange={(e) => setMinInput(e.target.value)}
				/>
				<span className={cn('text-xs', colors.classes.textSecondary)}>–</span>
				<input
					type="number"
					inputMode="decimal"
					className={inputBase}
					value={maxInput}
					min={config.min}
					max={config.max}
					step={config.step ?? 'any'}
					disabled={disabled || isModalOpen || isLocked}
					placeholder={t('Max')}
					onChange={(e) => setMaxInput(e.target.value)}
				/>
			</div>
		</div>
	);
};
